import {
    EditorContent,
    Extension,
    useCurrentEditor,
    useEditor,
} from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import ImageResizeBase from 'tiptap-extension-resize-image';

const ImageResize = ImageResizeBase.extend({
    renderHTML({ HTMLAttributes }) {
        const { containerStyle, wrapperStyle, ...imgAttrs } = HTMLAttributes;

        // Apply alignment/width from containerStyle directly to the image's style
        if (containerStyle) {
            const existingStyle = imgAttrs.style || '';
            imgAttrs.style = existingStyle
                ? `display: block; ${existingStyle}; ${containerStyle}`
                : `display: block; ${containerStyle}`;
        }

        return ['img', imgAttrs];
    },
});

import { useCallback, useEffect, useState } from 'react';
import Heading1Icon from './icons/Heading1Icon.jsx';
import Heading2Icon from './icons/Heading2Icon.jsx';
import Heading3Icon from './icons/Heading3Icon.jsx';
import BulletListIcon from './icons/BulletListIcon.jsx';
import NumberListIcon from './icons/NumberListIcon.jsx';
import ParagraphIcon from './icons/ParagraphIcon.jsx';
import {
    CodeBracketIcon,
    LinkIcon,
} from '@heroicons/react/24/outline/index.js';
import Modal from './Modal';
import TextInput from './TextInput';
import TextArea from './TextArea.jsx';
import { Placeholder } from '@tiptap/extension-placeholder';
import DropdownButton from '~/components/DropdownButton';
import { TextAlign } from '@tiptap/extension-text-align';
import AlignLeftIcon from '~/components/icons/AlignLeftIcon.jsx';
import AlignCenterIcon from '~/components/icons/AlignCenterIcon.jsx';
import AlignRightIcon from '~/components/icons/AlignRightIcon.jsx';
import { ImageUploadButton } from '~/components/tiptap-ui/image-upload-button'
import { ImageUploadNode } from '~/components/tiptap-node/image-upload-node'
import { handleImageUpload, MAX_FILE_SIZE } from '~/lib/tiptap-utils'
import * as Toast from '~/lib/Toast.js';

export function getMenuButtonClass(isActive) {
    return `px-3 py-2 min-w-6 min-h-8 inline-block hover:bg-primary-100 ${isActive ? 'bg-primary-100' : ''}`;
}

const MenuButton = ({
    runChainedCommand,
    onClick,
    isActive,
    children,
    editor,
}) => {
    return (
        <button
            onClick={() => {
                if (typeof onClick === 'function') {
                    onClick();
                } else {
                    runChainedCommand(editor.chain().focus()).run();
                }
            }}
            type="button"
            // disabled={
            //     !runChainedCommand(editor.can()
            //         .chain()
            //         .focus()
            //     ).run()
            // }
            className={getMenuButtonClass(isActive)}
        >
            {children}
        </button>
    );
};

const MenuBar = ({ editor, setIsHtml, compactMenu }) => {
    const [isLinkModalShowing, setIsLinkModalShowing] = useState(false);
    const [linkModalUrl, setLinkModalUrl] = useState('');

    const setLink = useCallback(
        url => {
            // cancelled
            if (url === null) {
                return;
            }

            // empty
            if (url === '') {
                editor
                    .chain()
                    .focus()
                    .extendMarkRange('link')
                    .unsetLink()
                    .run();

                return;
            }

            // update link
            editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: url })
                .run();
        },
        [editor],
    );

    function showLinkModal() {
        const previousUrl = editor.getAttributes('link').href;
        setLinkModalUrl(previousUrl);
        setIsLinkModalShowing(true);
    }

    function submitLink(ev) {
        ev.preventDefault();
        ev.stopPropagation();
        setLink(linkModalUrl);
        setIsLinkModalShowing(false);
    }

    if (!editor) {
        return null;
    }

    const iconClass = `w-4 h-4`;

    return (
        <div className="">
            <div className="border-b  rounded-t-lg bg-white flex justify-between items-center">
                <div className="flex items-start">
                    <DropdownButton
                        btnClassName={getMenuButtonClass(false)}
                        btnContent={
                            <Heading1Icon
                                title="Heading"
                                className={`${iconClass}`}
                            />
                        }
                        menuItemsClassName="left-0"
                        options={[
                            {
                                name: 'Heading 1',
                                icon: <Heading1Icon className={iconClass} />,
                                onClick: () =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleHeading({ level: 1 })
                                        .run(),
                            },
                            {
                                icon: <Heading2Icon className={iconClass} />,
                                name: 'Heading 2',
                                onClick: () =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleHeading({ level: 2 })
                                        .run(),
                            },
                            {
                                name: 'Heading 3',
                                icon: <Heading3Icon className={iconClass} />,
                                onClick: () =>
                                    editor
                                        .chain()
                                        .focus()
                                        .toggleHeading({ level: 3 })
                                        .run(),
                            },
                        ]}
                    />

                    <MenuButton
                        editor={editor}
                        isActive={editor.isActive('paragraph')}
                        runChainedCommand={c => c.setParagraph()}
                    >
                        <ParagraphIcon className={iconClass} />
                    </MenuButton>
                </div>

                <div>
                    <MenuButton
                        editor={editor}
                        isActive={editor.isActive('bold')}
                        runChainedCommand={c => c.toggleBold()}
                    >
                        <div className={`font-bold text-lg ${iconClass}`}>
                            B
                        </div>
                    </MenuButton>
                    {!compactMenu && (
                        <MenuButton
                            editor={editor}
                            isActive={editor.isActive('italic')}
                            runChainedCommand={c => c.toggleItalic()}
                        >
                            <div className={`italic text-lg ${iconClass}`}>i</div>
                        </MenuButton>
                    )}

                    <MenuButton
                        editor={editor}
                        isActive={editor.isActive('link')}
                        onClick={() => showLinkModal()}
                    >
                        <LinkIcon className={iconClass} />
                    </MenuButton>
                </div>

                <div>
                    <MenuButton
                        editor={editor}
                        isActive={editor.isActive('bulletList')}
                        runChainedCommand={c => c.toggleBulletList()}
                    >
                        <BulletListIcon className={iconClass} />
                    </MenuButton>

                    <MenuButton
                        editor={editor}
                        isActive={editor.isActive('orderedList')}
                        runChainedCommand={c => c.toggleOrderedList()}
                    >
                        <NumberListIcon className={iconClass} />
                    </MenuButton>

                    <DropdownButton
                        btnContent={
                            <>
                                {!editor.isActive({ textAlign: 'center' }) &&
                                    !editor.isActive({
                                        textAlign: 'right',
                                    }) && (
                                        <AlignLeftIcon
                                            title="Align"
                                            className={iconClass}
                                        />
                                    )}
                                {editor.isActive({ textAlign: 'center' }) && (
                                    <AlignCenterIcon
                                        title="Align"
                                        className={iconClass}
                                    />
                                )}
                                {editor.isActive({ textAlign: 'right' }) && (
                                    <AlignRightIcon
                                        title="Align"
                                        className={iconClass}
                                    />
                                )}
                            </>
                        }
                        btnClassName={getMenuButtonClass(false)}
                        options={[
                            {
                                name: 'Left',
                                icon: <AlignLeftIcon className={iconClass} />,
                                onClick: () =>
                                    editor
                                        .chain()
                                        .focus()
                                        .setTextAlign('left')
                                        .run(),
                            },
                            {
                                name: 'Center',
                                icon: <AlignCenterIcon className={iconClass} />,
                                onClick: () =>
                                    editor
                                        .chain()
                                        .focus()
                                        .setTextAlign('center')
                                        .run(),
                            },
                            {
                                name: 'Right',
                                icon: <AlignRightIcon className={iconClass} />,
                                onClick: () =>
                                    editor
                                        .chain()
                                        .focus()
                                        .setTextAlign('right')
                                        .run(),
                            },
                        ]}
                    />
                </div>

                <ImageUploadButton
                    editor={editor}
                    text=""
                    aria-label="Insert image"
                    hideWhenUnavailable={true}
                    showShortcut={false}
                    className={getMenuButtonClass(false)}
                />

                <div>
                    <MenuButton
                        editor={editor}
                        isActive={false}
                        onClick={() => setIsHtml(true)}
                    >
                        <CodeBracketIcon className={iconClass} />
                    </MenuButton>
                </div>
            </div>
            <Modal
                isOpen={isLinkModalShowing}
                onClose={() => setIsLinkModalShowing(false)}
            >
                <form onSubmit={submitLink}>
                    <TextInput
                        label="URL"
                        placeholder="https://google.com/"
                        value={linkModalUrl}
                        onChange={setLinkModalUrl}
                        autoFocus={true}
                    />
                    <div className="flex items-center mt-4 space-x-4">
                        <button className="btn-save">Save</button>
                        <button
                            type="button"
                            className="btn-light-pill-lg"
                            onClick={() => {
                                setLink('');
                                setIsLinkModalShowing(false);
                            }}
                        >
                            Remove Link
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

const CssExtension = Extension.create({
    addGlobalAttributes() {
        return [
            {
                // Extend the following extensions
                types: ['image', 'imageResize', 'paragraph', 'heading', 'link'],
                // … with those attributes
                attributes: {
                    style: {
                        default: '',
                        renderHTML: attributes => ({
                            style: attributes.style,
                        }),
                        parseHTML: element => element.style.cssText || '',
                    },
                },
            },
        ];
    },
});

const RichEditorBase = ({
    initialValue,
    showToolbar,
    onChange,
    className,
    extensions,
    setEditor,
    placeholder,
    noLinking,
    compactMenu,
}) => {
    // iframes don't render well in the editor, so default to html mode
    const [isHtml, setIsHtml] = useState(initialValue?.includes('<iframe'));
    const [htmlContent, setHtmlContent] = useState(initialValue);

    const allExtensions = [
        Document,
        Paragraph,
        Text,
        StarterKit,
        ImageResize,
        Placeholder.configure({
            placeholder: placeholder,
        }),
        CssExtension,
        TextAlign.configure({
            types: ['heading', 'paragraph'],
        }),
        ImageUploadNode.configure({
            accept: 'image/*',
            maxSize: MAX_FILE_SIZE,
            limit: 3,
            upload: handleImageUpload,
            type: 'imageResize', // this isn't in ts, but is used by upload node
            onError: (error) => Toast.error('Upload failed:' + error),
        }),
        ...(extensions || []),
    ];

    if (!noLinking) {
        allExtensions.push(
            Link.configure({
                openOnClick: false,
                autolink: true,
                defaultProtocol: 'https',
            }),
        );
    }

    const editor = useEditor({
        extensions: allExtensions,
        editorProps: {
            attributes: {
                class: `bg-white rounded-lg !border-gray-300 border ${showToolbar ? 'min-h-32 rounded-t-none' : ''} ${className}`,
            },
        },
        content: initialValue,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    useEffect(() => {
        if (typeof setEditor === 'function') {
            setEditor(editor);
        }
    }, [editor]);

    useEffect(() => {
        if (!editor) {
            return;
        }

        if (isHtml) {
            // Copy from editor
            setHtmlContent(editor.getHTML());
        } else {
            // Copy back to editor
            editor.commands.setContent(htmlContent);
        }
    }, [isHtml]);

    return (
        <div>
            {!!showToolbar && !isHtml && (
                <MenuBar
                    editor={editor}
                    setIsHtml={setIsHtml}
                    compactMenu={compactMenu}
                />
            )}
            {!isHtml && <EditorContent editor={editor} />}
            {isHtml && (
                <div>
                    <button
                        type="button"
                        onClick={() => setIsHtml(false)}
                        className="btn-light-pill-sm mb-1"
                    >
                        Switch back to editor
                    </button>
                    <TextArea
                        value={htmlContent}
                        placeholder={placeholder}
                        inputClassName="h-32"
                        onChange={value => {
                            setHtmlContent(value);
                            onChange(value);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default RichEditorBase;
